import {
  CsvExportParams,
  FilterChangedEvent,
  GetLocaleTextParams,
  GridOptions,
  GridReadyEvent,
  HeaderValueGetterParams,
  IRowNode,
  RowClickedEvent,
  SortChangedEvent,
  ValueFormatterParams,
  RowDataUpdatedEvent,
  GridApi,
} from '@ag-grid-community/core';
import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { LocalizedDatePipe } from '../shared/localized-date.pipe';
import { UserInfoService } from './user-info.service';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { LocalizedCurrencyPipe } from '../shared/localized-currency.pipe';

const CACHE_SIZE = 1;

@Injectable()
export abstract class OverviewBaseService {
  protected csvExportParams = {
    columnSeparator: ';',
    suppressQuotes: false,
  } as CsvExportParams;

  private filters: ExternalFilterDefinition[] = [];

  onFiltersReseted = new EventEmitter<void>();
  onExternalFilterChanged = new EventEmitter<void>();
  onExportAsCsv = new EventEmitter<void>();

  currentUsername$ = new BehaviorSubject<string>('');

  gridApi?: GridApi;
  gridReady$ = new BehaviorSubject<GridReadyEvent | null>(null);

  subs = new SubSink();
  gridSubs = new SubSink();

  enabledExternalFilters$: {
    key: string;
    subject: BehaviorSubject<boolean>;
    defaultState?: boolean;
  }[] = [];

  filterApplied$ = new BehaviorSubject<boolean>(false);

  constructor(
    protected router: Router,
    protected userInfoService: UserInfoService,
    protected translateService: TranslateService,
    private localizedDatePipe: LocalizedDatePipe,
    private localizedCurrencyPipe: LocalizedCurrencyPipe
  ) {
    this.subs.sink = this.userInfoService
      .getUserInfo()
      .pipe(map(m => m?.id ?? ''))
      .subscribe(s => {
        this.currentUsername$.next(s);
      });
  }

  //TODO: add options for paging if needed
  abstract query(): Observable<XatListDto | any[]>;

  abstract rowClicked(event: RowClickedEvent): void;

  bindGridEvents(options: GridOptions): void {
    options.onSortChanged = this.sortChanged.bind(this);
    options.onGridReady = this.gridReady.bind(this);
    options.onFilterChanged = this.filterChanged.bind(this);
    options.onRowDataUpdated = this.rowDataUpdated.bind(this);
    options.onRowClicked = this.rowClicked.bind(this);
    options.doesExternalFilterPass = this.doesExternalFilterPass.bind(this);
    options.isExternalFilterPresent = this.isExternalFilterPresent.bind(this);

    options.getLocaleText = this.getLocaleText.bind(this);
  }

  addExternalFilters(filterDefs: ExternalFilterDefinition[]): void {
    filterDefs.forEach(f => {
      this.filters.push(f);

      const tmp = {
        key: f.key,
        subject: new BehaviorSubject<boolean>(f.defaultState ?? false),
        defaultState: f.defaultState,
      };

      this.enabledExternalFilters$.push(tmp);
      this.subs.sink = tmp.subject.subscribe(() =>
        this.onExternalFilterChanged.emit()
      );
    });
  }

  quickFilterSearchText$ = new BehaviorSubject<string | null | undefined>('');

  resetFilters(): void {
    this.quickFilterSearchText$.next('');
    this.enabledExternalFilters$
      .filter(f => f.subject.value)
      .forEach(f => f.subject.next(f.defaultState ?? false));

    this.onFiltersReseted.emit();
  }

  filterDrawerMenuOpen$ = new BehaviorSubject<boolean>(true);
  toggleFilterDrawer(): void {
    this.filterDrawerMenuOpen$.next(!this.filterDrawerMenuOpen$.value);
  }

  protected refreshRowDataCache$ = new BehaviorSubject<void>(undefined);
  refreshRowDataCache(): void {
    this.refreshRowDataCache$.next();
  }

  private rowDataCache$!: Observable<any[][]>;
  get rowData(): Observable<any[]> {
    if (!this.rowDataCache$) {
      this.rowDataCache$ = this.refreshRowDataCache$.pipe(
        switchMap(() => this.query()),
        map(m => {
          if (m instanceof Array) {
            return m;
          } else {
            return m.result;
          }
        }),
        shareReplay(CACHE_SIZE)
      );
    }

    return this.rowDataCache$;
  }

  isExternalFilterPresent(): boolean {
    if (!this.enabledExternalFilters$) return false;

    const result = this.enabledExternalFilters$
      .map(m => m.subject)
      .reduce((prev, cur) => prev || cur.value, false);

    return result;
  }

  doesExternalFilterPass(node: IRowNode): boolean {
    const enabledFilters = this.enabledExternalFilters$
      .filter(f => f.subject.value)
      .map(m => m.key);

    const result = this.filters
      .filter(f => enabledFilters.includes(f.key))
      .map(m => m.filterExpression)
      .reduce((prev, cur) => prev && cur(node), true);

    return result;
  }

  displayedRowDataCount$ = new BehaviorSubject<number>(0);
  rowDataUpdated(event: RowDataUpdatedEvent) {
    this.displayedRowDataCount$.next(event.api.getDisplayedRowCount());
  }

  columState$ = new BehaviorSubject<any>(null);
  sortChanged(event: SortChangedEvent) {
    this.columState$.next(event.api.getColumnState());
  }

  filterModelState$ = new BehaviorSubject<any>(null);
  filterChanged(event: FilterChangedEvent) {
    this.filterModelState$.next(event.api.getFilterModel());
    this.displayedRowDataCount$.next(event.api.getDisplayedRowCount());
  }

  gridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;

    this.gridSubs.unsubscribe();

    this.gridSubs.sink = this.rowData.subscribe(s => {
      event.api?.updateGridOptions({
        rowData: s,
      });
      event.api.setFilterModel(this.filterModelState$.value);
      event.api.applyColumnState({
        state: this.columState$.value,
        applyOrder: true,
      });
    });

    this.gridSubs.sink = this.onExternalFilterChanged.subscribe(() => {
      event.api.onFilterChanged();
    });

    this.gridSubs.sink = this.quickFilterSearchText$.subscribe(s => {
      event.api?.updateGridOptions({
        quickFilterText: s ?? '',
      });
    });

    this.gridSubs.sink = this.onFiltersReseted.subscribe(() => {
      event.api.setFilterModel(null);
    });

    this.gridSubs.sink = this.onExportAsCsv.subscribe(() => {
      event.api.exportDataAsCsv(this.csvExportParams);
    });

    this.gridSubs.sink = combineLatest([
      this.displayedRowDataCount$,
      this.rowData,
    ])
      .pipe(
        map(([currentCount, data]) => {
          return data.length > currentCount;
        })
      )
      .subscribe(s => {
        this.filterApplied$.next(s);
      });

    event.api.setFilterModel(this.filterModelState$.value);
    event.api.applyColumnState({
      state: this.columState$.value,
      applyOrder: true,
    });

    this.gridReady$.next(event);
  }

  getLocaleText(params: GetLocaleTextParams<string, string>): string {
    return this.translateService.instant(`AG_GRID.${params.key}`);
  }

  /**
   * DONT USE THIS: it will break ag-grids internal date filters
   * @param params
   * @returns
   */
  // formattedDateValueGetter(params: ValueGetterParams): string {
  //     if (params.colDef?.field) {
  //         let value = params.data[params.colDef.field];
  //         return this.localizedDatePipe.transform(value);
  //     }

  //     return '';
  // }

  durationInDaysValueFormatter(params: ValueFormatterParams): string {
    if (params.value) {
      const value = Math.round(+params.value / 86400000);

      const suffix =
        value == 1
          ? this.translateService.instant(_('SHARED.UNITS.TIME.DAY.SINGULAR'))
          : this.translateService.instant(_('SHARED.UNITS.TIME.DAY.PLURAL'));

      return `${value} ${suffix}`;
    }

    return '';
  }

  dateTimeValueFormatter(params: ValueFormatterParams): string {
    if (params.value) {
      const value = params.value;
      return this.localizedDatePipe.transform(value, 'medium');
    }

    return '';
  }

  dateValueFormatter(params: ValueFormatterParams): string {
    if (params.value) {
      const value = params.value;
      return this.localizedDatePipe.transform(value);
    }

    return '';
  }

  currencyValueFormatter(params: ValueFormatterParams): string {
    if (!params.value) return '';
    if (params.context.currency as string) {
      const currencyCode = params.context.currency ?? 'USD';
      return this.localizedCurrencyPipe.transform(params.value, currencyCode);
    }

    return this.localizedCurrencyPipe.transform(params.value);
  }

  localizeHeader(parameters: HeaderValueGetterParams): string {
    const headerIdentifier = parameters.colDef.headerName;
    if (headerIdentifier)
      return this.translateService.instant(headerIdentifier);

    return parameters.colDef.headerName || '';
  }

  dateColumnFilterComparator(
    filterLocalDateAtMidnight: Date,
    cellValue: string
  ): number {
    const dateAsString = cellValue;
    if (dateAsString == null) return 0;

    const cellDate = new Date(dateAsString);

    if (cellDate < filterLocalDateAtMidnight) {
      return -1;
    } else if (cellDate > filterLocalDateAtMidnight) {
      return 1;
    }

    return 0;
  }
}

export interface ExternalFilterDefinition {
  key: string;
  defaultState?: boolean;
  filterExpression: (node: IRowNode) => boolean;
}

export interface XatListDto {
  /**
   * Total row count
   */
  count: number;
  result: any[];
}
